/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import React, {
  useMemo,
  useCallback,
  useEffect,
  useState,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { TableUtility } from '@utility/dynamic-table';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { getExportFileName, getExportFileNameWithDate } from '../Delivery/utils';
import { toast } from 'react-toastify';
import { useAuth } from '../../Hooks/useAuth';
import UseAxios from '../../Api/http/http';
import { serviceName } from '../../Api/resources/serviceConstants';
import { EMPLOYEE_MASTER } from '../../Api/resources/endPoint';
import alertIcon from '../../Assets/images/svg/alert_icon.svg';
import {
  appendServiceNameToURL,
  appendServicePortToURL,
} from '../../Api/resources/serviceURLs';
import {
  createEmployee,
  updateEmployee,
  deleteEmployee,
  getVerticals,
  getRegions,
  getCountries,
  getDepartments,
  getDesignations,
  getExperienceCategories,
  getLocations,
  getActiveAllocations,
} from '../../Api/request/employeeMaster';
import { employeeManagementTableConfig } from './employeeManagementTableConfig';
import './employeeManagement.scss';

const fetchBaseURL = () => {
  const serviceURL = appendServiceNameToURL(serviceName.LANDING_MS);
  const servicePort = appendServicePortToURL(serviceName.LANDING_MS);
  const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  if (process.env.NODE_ENV === 'production') {
    return `${baseUrl}${serviceURL}/api${EMPLOYEE_MASTER}`;
  }
  return `${baseUrl}:${servicePort}${serviceURL}/api${EMPLOYEE_MASTER}`;
};

const baseURL = fetchBaseURL();
const PAGE_SIZE = 10;

// Hardcoded Dropdown Options sent to DB
const JOINING_TYPES = [
  { label: 'Regular', value: 'R' },
  { label: 'Contract', value: 'C' },
  { label: 'Contract to Hire', value: 'H' },
];

const EMP_TYPES = [
  { label: 'Fresher', value: 'Fresher' },
  { label: 'Regular', value: 'Regular' },
  { label: 'Premium', value: 'Premium' },
  { label: 'Niche', value: 'Niche' },
];

const INITIAL_FORM = {
  emp_no: '',
  emp_id: '',
  first_name: '',
  last_name: '',
  email_id: '',
  vertical: '',
  designation: '',
  joining_date: '',
  joining_type: '',
  primary_skills: '',
  secondry_skills: '',
  emp_type: '',
  billability: '',
  experience: '',
  remarks: '',
  exit_date: '',
  is_active: '',
  ctc: '',
  email_sent: '',
  shadow_end_date: '',
  jira_user_id: '-',
  overall_experience: '',
  department_id: '',
  region_id: '',
  country_id: '',
  emp_vertical_id: '',
  emp_designation_id: '',
  effective_from: '',
  experience_category_id: '',
  location_id: '',
};

const formatDateForInput = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date)) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatExitDateForInput = (dateStr) => {
  if (!dateStr || dateStr.startsWith('9999') || dateStr.startsWith('1999')) return '';
  return formatDateForInput(dateStr);
};

const DeleteConfirmationModal = ({ empName, onClose, onConfirm }) =>
  createPortal(
    <div className="emp-mgmt-modal-backdrop" style={{ zIndex: 2000 }}>
      <div className="emp-mgmt-modal delete-employee-confirmation">
        <div className="emp-mgmt-modal__header delete-employee-header">
          <span style={{ fontSize: '18px', color: '#4B5563' }}>Delete Employee</span>
        </div>
        <div className="emp-mgmt-modal__body delete-employee-body">
          <img src={alertIcon} alt="Alert Icon" style={{ width: '24px', height: '24px' }} />
          <span style={{ fontSize: '15px', color: '#374151' }}>
            Are you sure you want to delete <b>{empName}</b>?
          </span>
        </div>
        <div className="emp-mgmt-modal__footer" style={{ borderTop: 'none', paddingTop: '0' }}>
          <button
            type="button"
            onClick={onClose}
            className="delete-cancel-btn">
            Cancel
          </button>
          <button
            type="button"
            className="emp-mgmt-modal__btn emp-mgmt-modal__btn--primary"
            onClick={onConfirm}
            style={{ background: '#8B5CF6', border: 'none' }}
          >
            Delete
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );

const AllocationWarningModal = ({ projects, onClose }) =>
  createPortal(
    <div className="emp-mgmt-modal-backdrop emp-mgmt-modal-backdrop--warning">
      <div className="emp-mgmt-modal allocation-warning-confirmation">
        <div className="emp-mgmt-modal__header delete-employee-header">
          <span className="allocation-warning-header-text">Active Allocation Warning</span>
        </div>
        <div className="emp-mgmt-modal__body allocation-warning-body">
          <div className="allocation-warning-title-row">
            <img src={alertIcon} alt="Alert Icon" className="allocation-warning-alert-icon" />
            <span className="allocation-warning-title-text">
              Cannot set exit date
            </span>
          </div>
          <span className="allocation-warning-desc">
            This employee is currently allocated to the following active projects:
          </span>
          <ul className="allocation-warning-projects-list">
            {projects.map((proj, idx) => (
              <li key={idx}><b>{proj}</b></li>
            ))}
          </ul>
          <span className="allocation-warning-footer-note">
            Please update their allocation dates before setting their exit date.
          </span>
        </div>
        <div className="emp-mgmt-modal__footer">
          <button
            type="button"
            className="emp-mgmt-modal__btn emp-mgmt-modal__btn--primary allocation-warning-ok-btn"
            onClick={onClose}
          >
            OK
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );

/* eslint-disable-next-line react/prop-types */
const EmployeeFormModal = ({
  mode,
  form,
  fieldErrors,
  verticals,
  regions,
  countries,
  departments,
  designations,
  experienceCategories,
  locations,
  onClose,
  onSubmit,
  onChange,
  onViewHistory,
}) => {
  const availableCountries = form.region_id
    ? countries.filter((value) => String(value.region_id) === String(form.region_id))
    : [];
  return createPortal(
    <div className="emp-mgmt-modal-backdrop">
      <form className="emp-mgmt-modal" onSubmit={onSubmit}>
        <div className="emp-mgmt-modal__header">
          {mode === 'edit' ? (
            <span>Edit Employee Details</span>
          ) : (
            <span>Add Employee Details</span>
          )}
          {mode === 'edit' && (
            <button
              type="button"
              className="emp-mgmt-view-history-link"
              onClick={onViewHistory}
            >
              View Change History
            </button>
          )}
        </div>
        <div className="emp-mgmt-modal__body emp-mgmt-modal__body--scroll">
          <div className="emp-mgmt-modal__grid">
            <div className="emp-mgmt-modal__field">
              <label
                htmlFor="emp_id"
                className={fieldErrors?.emp_id ? 'is-error' : ''}
              >
                Employee ID<span className="required-asterisk">*</span>
              </label>
              <input
                id="emp_id"
                name="emp_id"
                type="text"
                placeholder="Enter Employee ID"
                value={form.emp_id}
                onChange={onChange}
                className={fieldErrors?.emp_id ? 'is-error' : ''}
                disabled={mode === 'edit'}
              />
            </div>
            <div className="emp-mgmt-modal__field">
              <label
                htmlFor="first_name"
                className={fieldErrors?.first_name ? 'is-error' : ''}
              >
                First Name<span className="required-asterisk">*</span>
              </label>
              <input
                id="first_name"
                name="first_name"
                type="text"
                placeholder="Enter First Name"
                value={form.first_name}
                onChange={onChange}
                className={fieldErrors?.first_name ? 'is-error' : ''}
              />
            </div>
            <div className="emp-mgmt-modal__field">
              <label
                htmlFor="last_name"
                className={fieldErrors?.last_name ? 'is-error' : ''}
              >
                Last Name<span className="required-asterisk">*</span>
              </label>
              <input
                id="last_name"
                name="last_name"
                type="text"
                placeholder="Enter Last Name"
                value={form.last_name}
                onChange={onChange}
                className={fieldErrors?.last_name ? 'is-error' : ''}
              />
            </div>
            <div className="emp-mgmt-modal__field">
              <label
                htmlFor="email_id"
                className={fieldErrors?.email_id ? 'is-error' : ''}
              >
                Email Id<span className="required-asterisk">*</span>
              </label>
              <input
                id="email_id"
                name="email_id"
                type="email"
                placeholder="Enter Email Id"
                value={form.email_id}
                onChange={onChange}
                className={fieldErrors?.email_id ? 'is-error' : ''}
              />
            </div>
            <div className="emp-mgmt-modal__field">
              <label
                htmlFor="joining_date"
                className={fieldErrors?.joining_date ? 'is-error' : ''}
              >
                Date of Joining<span className="required-asterisk">*</span>
              </label>
              <input
                id="joining_date"
                name="joining_date"
                type="date"
                value={form.joining_date}
                onChange={onChange}
                className={fieldErrors?.joining_date ? 'is-error' : ''}
              />
            </div>
            <div className="emp-mgmt-modal__field">
              <label htmlFor="experience" style={{ overflow: 'visible' }}>
                Years of exp at the time of joining
                <span className="emp-mgmt-info-icon">
                  <i className="pi pi-info-circle" />
                  <div className="emp-mgmt-tooltip">
                    Two decimal places are allowed with max value 11.<br />
                    Eg: 2 year 1 month can be written as 2.1 or 2.01.<br />
                    2 year 9 month can be written as 2.9.<br />
                    2 year 10 month can be written as 2.10.<br />
                    2 year 11 month can be written as 2.11.
                  </div>
                </span>
              </label>
              <input
                id="experience"
                name="experience"
                type="text"
                placeholder="Enter Years of exp."
                value={form.experience}
                onChange={onChange}
              />
            </div>
            <div className="emp-mgmt-modal__field">
              <label
                htmlFor="emp_designation_id"
                className={fieldErrors?.emp_designation_id ? 'is-error' : ''}
              >
                Designation<span className="required-asterisk">*</span>
              </label>
              <select
                id="emp_designation_id"
                name="emp_designation_id"
                value={form.emp_designation_id}
                onChange={onChange}
                className={fieldErrors?.emp_designation_id ? 'is-error' : ''}
              >
                <option value="">Select</option>
                {designations.map((designation, index) => (
                  <option
                    key={designation.value || index}
                    value={designation.value}
                  >
                    {designation.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="emp-mgmt-modal__field">
              <label
                htmlFor="emp_vertical_id"
                className={fieldErrors?.emp_vertical_id ? 'is-error' : ''}
              >
                Vertical<span className="required-asterisk">*</span>
              </label>
              <select
                id="emp_vertical_id"
                name="emp_vertical_id"
                value={form.emp_vertical_id}
                onChange={onChange}
                className={fieldErrors?.emp_vertical_id ? 'is-error' : ''}
              >
                <option value="">Select</option>
                {verticals.map((vertical, index) => (
                  <option key={vertical.value || index} value={vertical.value}>
                    {vertical.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="emp-mgmt-modal__field">
              <label htmlFor="joining_type">
                Resource Type<span className="required-asterisk">*</span>
              </label>
              <select
                id="joining_type"
                name="joining_type"
                value={form.joining_type}
                onChange={onChange}
                className={form.joining_type === '' ? 'is-placeholder' : ''}
              >
                <option value="">Select</option>
                {JOINING_TYPES.map((rt, index) => (
                  <option key={index} value={rt.value}>
                    {rt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="emp-mgmt-modal__field">
              <label
                htmlFor="primary_skills"
                className={fieldErrors?.primary_skills ? 'is-error' : ''}
              >
                Primary Skills<span className="required-asterisk">*</span>
              </label>
              <input
                id="primary_skills"
                name="primary_skills"
                type="text"
                placeholder="Enter Primary Skills"
                value={form.primary_skills}
                onChange={onChange}
                className={fieldErrors?.primary_skills ? 'is-error' : ''}
              />
            </div>
            <div className="emp-mgmt-modal__field">
              <label htmlFor="secondry_skills">Secondary Skills</label>
              <input
                id="secondry_skills"
                name="secondry_skills"
                type="text"
                placeholder="Enter Secondary Skills"
                value={form.secondry_skills}
                onChange={onChange}
              />
            </div>
            <div className="emp-mgmt-modal__field">
              <label htmlFor="emp_type">Employment Type</label>
              <select
                id="emp_type"
                name="emp_type"
                value={form.emp_type}
                onChange={onChange}
                className={form.emp_type === '' ? 'is-placeholder' : ''}
              >
                <option value="">Select</option>
                {EMP_TYPES.map((empString, index) => (
                  <option key={index} value={empString.value}>
                    {empString.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="emp-mgmt-modal__field">
              <label
                htmlFor="shadow_end_date"
                className={fieldErrors?.shadow_end_date ? 'is-error' : ''}
              >
                Shadow End Date
              </label>
              <input
                id="shadow_end_date"
                name="shadow_end_date"
                type="date"
                value={form.shadow_end_date}
                onChange={onChange}
                className={fieldErrors?.shadow_end_date ? 'is-error' : ''}
              />
            </div>
            <div className="emp-mgmt-modal__field">
              <label
                htmlFor="jira_user_id"
                className={fieldErrors?.jira_user_id ? 'is-error' : ''}
              >
                Jira User Id<span className="required-asterisk">*</span>
              </label>
              <input
                id="jira_user_id"
                name="jira_user_id"
                type="text"
                placeholder="Enter Jira User Id"
                value={form.jira_user_id}
                onChange={onChange}
                className={fieldErrors?.jira_user_id ? 'is-error' : ''}
              />
            </div>
            <div className="emp-mgmt-modal__field">
              <label
                htmlFor="department_id"
                className={fieldErrors?.department_id ? 'is-error' : ''}
              >
                Department<span className="required-asterisk">*</span>
              </label>
              <select
                id="department_id"
                name="department_id"
                value={form.department_id}
                onChange={onChange}
                className={fieldErrors?.department_id ? 'is-error' : ''}
              >
                <option value="">Select</option>
                {departments.map((department, index) => (
                  <option
                    key={department.value || index}
                    value={department.value}
                  >
                    {department.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="emp-mgmt-modal__field">
              <label
                htmlFor="region_id"
                className={fieldErrors?.region_id ? 'is-error' : ''}
              >
                Region<span className="required-asterisk">*</span>
              </label>
              <select
                id="region_id"
                name="region_id"
                value={form.region_id}
                onChange={onChange}
                className={form.region_id === '' ? 'is-placeholder' : ''}
              >
                <option value="">Select</option>
                {regions.map((region, index) => (
                  <option key={region.value || index} value={region.value}>
                    {region.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="emp-mgmt-modal__field">
              <label
                htmlFor="country_id"
                className={fieldErrors?.country_id ? 'is-error' : ''}
              >
                Country<span className="required-asterisk">*</span>
              </label>
              <select
                id="country_id"
                name="country_id"
                value={form.country_id}
                onChange={onChange}
                className={form.country_id === '' ? 'is-placeholder' : ''}
                disabled={!form.region_id}
              >
                <option value="">Select</option>
                {availableCountries.map((country, index) => (
                  <option key={country.value || index} value={country.value}>
                    {country.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="emp-mgmt-modal__field">
              <label
                htmlFor="experience_category_id"
                className={fieldErrors?.experience_category_id ? 'is-error' : ''}
              >
                Experience Category<span className="required-asterisk">*</span>
              </label>
              <select
                id="experience_category_id"
                name="experience_category_id"
                value={form.experience_category_id}
                onChange={onChange}
                className={
                  // eslint-disable-next-line no-nested-ternary
                  fieldErrors?.experience_category_id
                    ? 'is-error'
                    : (form.experience_category_id === '' ? 'is-placeholder' : '')
                }
              >
                <option value="">Select</option>
                {experienceCategories.map((cat, index) => (
                  <option key={cat.value || index} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="emp-mgmt-modal__field">
              <label
                htmlFor="location_id"
                className={fieldErrors?.location_id ? 'is-error' : ''}
              >
                Location<span className="required-asterisk">*</span>
              </label>
              <select
                id="location_id"
                name="location_id"
                value={form.location_id}
                onChange={onChange}
                className={fieldErrors?.location_id ? 'is-error' : (form.location_id === '' ? 'is-placeholder' : '')}
              >
                <option value="">Select</option>
                {locations.map((loc, index) => (
                  <option key={loc.value || index} value={loc.value}>
                    {loc.label}
                  </option>
                ))}
              </select>
            </div>
            {mode === 'edit' && (
              <>
                <div className="emp-mgmt-modal__field">
                  <label
                    htmlFor="exit_date"
                    className={fieldErrors?.exit_date ? 'is-error' : ''}
                  >
                    Exit Date
                  </label>
                  <input
                    id="exit_date"
                    name="exit_date"
                    type="date"
                    value={form.exit_date || ''}
                    onChange={onChange}
                    className={fieldErrors?.exit_date ? 'is-error' : ''}
                  />
                </div>
                <div className="emp-mgmt-modal__field">
                  <label
                    htmlFor="is_active"
                    className={fieldErrors?.is_active ? 'is-error' : ''}
                  >
                    Status
                  </label>
                  <select
                    id="is_active"
                    name="is_active"
                    value={form.is_active || ''}
                    onChange={onChange}
                    className={fieldErrors?.is_active ? 'is-error' : ''}
                  >
                    <option value="Y">Active</option>
                    <option value="R">Resigned</option>
                    <option value="N">Inactive</option>
                  </select>
                </div>
                <div className="emp-mgmt-modal__field">
                  <label
                    htmlFor="effective_from"
                    className={fieldErrors?.effective_from ? 'is-error' : ''}
                  >
                    Effective From
                  </label>
                  <input
                    id="effective_from"
                    name="effective_from"
                    type="date"
                    value={form.effective_from || ''}
                    onChange={onChange}
                    className={fieldErrors?.effective_from ? 'is-error' : ''}
                  />
                </div>
              </>
            )}
          </div>
        </div>
        <div className="emp-mgmt-modal__footer">
          <button
            type="button"
            className="emp-mgmt-modal__btn emp-mgmt-modal__btn--ghost"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="emp-mgmt-modal__btn emp-mgmt-modal__btn--primary"
          >
            {mode === 'edit' ? 'Update' : 'Save'}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
};

const REQUIRED_FIELDS = [
  'emp_id',
  'first_name',
  'last_name',
  'email_id',
  'joining_date',
  'emp_designation_id',
  'emp_vertical_id',
  'joining_type',
  'primary_skills',
  'department_id',
  'region_id',
  'country_id',
  'experience_category_id',
  'location_id',
  'jira_user_id',
];

const validateForm = (form) => {
  const errors = {};
  REQUIRED_FIELDS.forEach((field) => {
    if (form[field] === undefined || form[field] === null || String(form[field]).trim() === '') {
      errors[field] = 'Required';
    }
  });
  // eslint-disable-next-line no-control-regex
  // Validate Jira Account ID format
  // single hyphen or alphanumeric with optional hyphens throughout
  const jiraIdRegex = /^(-|[a-zA-Z0-9:]+(-[a-zA-Z0-9:]+)*)$/;
  if (!form.jira_user_id || form.jira_user_id.trim() === '') {
    errors.jira_user_id = 'Jira User ID is required';
  } else if (!jiraIdRegex.test(form.jira_user_id.trim())) {
    errors.jira_user_id = 'Please enter a valid Jira Account ID or "-"';
  }
  return errors;
};

const EmployeeManagement = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();

  // Explicit Permissions Check
  const isSuperAdmin = useMemo(() => {
    return userProfile?.roleDetails?.some((role) =>
      role.roleName?.toLowerCase().includes('super admin'),
    );
  }, [userProfile]);

  const empPermissions = userProfile?.permissions?.['resource:employee-management'] || [];
  const canAdd = isSuperAdmin || empPermissions.includes('add') || empPermissions.includes('create');
  const canEdit = isSuperAdmin || empPermissions.includes('update');
  const canDelete = isSuperAdmin || empPermissions.includes('delete');

  const [dialogMode, setDialogMode] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [allocationWarningProjects, setAllocationWarningProjects] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [verticals, setVerticals] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [regions, setRegions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [experienceCategories, setExperienceCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const tableStateRef = useRef(null);

  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0 });

  const refreshTable = useCallback(() => {
    const tableState = tableStateRef.current?.();
    if (tableState && tableState.fetchTableData) {
      tableState.fetchTableData(
        tableState.filters,
        tableState.pagination,
        tableState.searchQuery,
      );
    }
  }, []);

  const handlePaginationChange = useCallback((newPagination) => {
    setPagination(newPagination);
  }, []);

  const handleFilteredApiResponse = useCallback((response) => {
    if (!response) return response;

    try {
      const payload = response.data?.data || response.data || {};
      const formatRowExitDate = (row) => {
        if (row && row.exit_date) {
          if (row.exit_date.startsWith('9999') || row.exit_date.startsWith('1999')) {
            row.exit_date = '';
          } else {
            row.exit_date = row.exit_date.split('T')[0];
          }
        }
        if (row && row.is_active) {
          row.is_active = row.is_active === 'Y' ? 'Active' : (row.is_active === 'R' ? 'Resigned' : 'Inactive');
        }
      };

      if (Array.isArray(payload.rows)) {
        payload.rows.forEach(formatRowExitDate);
      } else if (Array.isArray(payload)) {
        payload.forEach(formatRowExitDate);
      }

      let tableConfigArray = payload.tableConfiguration || [];

      if (tableConfigArray.length > 0) {
        tableConfigArray = tableConfigArray.map((col) => {
          let modifiedCol = { ...col };

          if (modifiedCol.field === 'action') {
            const allowedActions = [];
            if (canEdit) allowedActions.push('edit');
            if (canDelete) allowedActions.push('delete');
            modifiedCol = {
              ...modifiedCol,
              actions: allowedActions,
              visible: allowedActions.length > 0, // Hides column if no permissions
            };
          }

          // DP2-6685 Fix: Inject options to allow View Applied Filters to map IDs to labels properly
          if (modifiedCol.menuItems && Array.isArray(modifiedCol.menuItems)) {
            modifiedCol.menuItems = modifiedCol.menuItems.map((menuGroup) => {
              if (menuGroup.items && Array.isArray(menuGroup.items)) {
                menuGroup.items = menuGroup.items.map((item) => {
                  if (item.type === 'filter') {
                    if (item.field === 'employee_vertical_name' && verticals?.length > 0) {
                      item.options = verticals;
                    } else if (item.field === 'designation' && designations?.length > 0) {
                      item.options = designations;
                    } else if (item.field === 'experience_category' && experienceCategories?.length > 0) {
                      item.options = experienceCategories;
                    } else if (item.field === 'location' && locations?.length > 0) {
                      item.options = locations;
                    } else if (item.field === 'full_name') {
                      const rows = Array.isArray(payload.tableData)
                        ? payload.tableData
                        : (Array.isArray(payload.rows)
                          ? payload.rows
                          : (Array.isArray(payload) ? payload : []));
                      const nameOptions = rows
                        .filter((row) => row.emp_id && (row.full_name || (row.first_name || row.last_name)))
                        .map((row) => {
                          const fullName = row.full_name || [row.first_name, row.last_name].filter(Boolean).join(' ');
                          return {
                            value: row.emp_id,
                            label: fullName.trim(),
                          };
                        });
                      if (nameOptions.length > 0) {
                        item.options = nameOptions;
                      }
                    } else if (item.field === 'emp_id') {
                      const rows = Array.isArray(payload.tableData)
                        ? payload.tableData
                        : (Array.isArray(payload.rows)
                          ? payload.rows
                          : (Array.isArray(payload) ? payload : []));
                      const idOptions = rows
                        .filter((row) => row.emp_id)
                        .map((row) => ({
                          value: row.emp_id,
                          label: row.emp_id,
                        }));
                      if (idOptions.length > 0) {
                        item.options = idOptions;
                      }
                    }
                  }
                  return item;
                });
              }
              return menuGroup;
            });
          }

          return modifiedCol;
        });

        // Apply modified config back to the response
        if (response.data?.data) {
          response.data.data.tableConfiguration = tableConfigArray;
        } else if (response.data) {
          response.data.tableConfiguration = tableConfigArray;
        }
      }

      // Existing Pagination Logic
      const paginationData = response.meta || response.data?.meta || {};
      const safePage = Number(paginationData.page) || 1;
      const safeLimit = Number(paginationData.pageSize || paginationData.limit) || PAGE_SIZE;
      const safeTotal = Number(paginationData.total) || 0;

      setPagination((prev) => ({
        ...prev,
        page: safePage,
        limit: safeLimit,
        total: safeTotal,
        totalPages: paginationData.totalPages,
      }));

      response.meta = {
        ...paginationData,
        page: safePage,
        limit: safeLimit,
        pageSize: safeLimit,
        total: safeTotal,
        totalRecords: safeTotal,
      };

      if (response.data) {
        response.data.meta = response.meta;
        response.data.pagination = response.meta;
      }

    } catch (err) {
      console.error('Pagination formatting error:', err);
    }
    return response;
  }, [canEdit, canDelete, verticals, designations, experienceCategories, locations]);


  const fetchRegions = useCallback(async () => {
    try {
      const res = await UseAxios(getRegions(), () => { });
      const rawData = res?.data?.data || res?.data || [];
      setRegions(Array.isArray(rawData) ? rawData : []);
    } catch (error) {
      setRegions([]);
    }
  }, []);

  const fetchCountries = useCallback(async () => {
    try {
      const res = await UseAxios(getCountries(), () => { });
      const rawData = res?.data?.data || res?.data || [];
      setCountries(Array.isArray(rawData) ? rawData : []);
    } catch (error) {
      setCountries([]);
    }
  }, []);

  const fetchDepartments = useCallback(async () => {
    try {
      const res = await UseAxios(getDepartments(), () => { });
      const rawData = res?.data?.data || res?.data || [];
      setDepartments(Array.isArray(rawData) ? rawData : []);
    } catch (error) {
      setDepartments([]);
    }
  }, []);

  const fetchDesignations = useCallback(async () => {
    try {
      const res = await UseAxios(getDesignations(), () => { });
      const rawData = res?.data?.data || res?.data || [];
      setDesignations(Array.isArray(rawData) ? rawData : []);
    } catch (error) {
      setDesignations([]);
    }
  }, []);

  const loadVerticals = useCallback(async () => {
    try {
      const res = await UseAxios(getVerticals(), () => { });
      const rawData = res?.data?.data || res?.data || [];
      setVerticals(Array.isArray(rawData) ? rawData : []);
    } catch (error) {
      setVerticals([]);
    }
  }, []);

  const fetchExperienceCategories = useCallback(async () => {
    try {
      const res = await UseAxios(getExperienceCategories(), () => { });
      const rawData = res?.data?.data || res?.data || [];
      setExperienceCategories(Array.isArray(rawData) ? rawData : []);
    } catch (error) {
      setExperienceCategories([]);
    }
  }, []);

  const fetchLocations = useCallback(async () => {
    try {
      const res = await UseAxios(getLocations(), () => { });
      const rawData = res?.data?.data || res?.data || [];
      setLocations(Array.isArray(rawData) ? rawData : []);
    } catch (error) {
      setLocations([]);
    }
  }, []);

  useEffect(() => {
    loadVerticals();
    fetchRegions();
    fetchCountries();
    fetchDepartments();
    fetchDesignations();
    fetchExperienceCategories();
    fetchLocations();
  }, [
    loadVerticals,
    fetchRegions,
    fetchCountries,
    fetchDepartments,
    fetchDesignations,
    fetchExperienceCategories,
    fetchLocations,
  ]);

  const handleAddClick = useCallback(() => {
    setForm(INITIAL_FORM);
    setFieldErrors({});
    setDialogMode('add');
  }, []);

  const handleEditClick = useCallback((emp) => {
    setSelectedEmployee(emp);
    const rawStatus = emp.isActive || emp.is_active || 'Y';
    const mappedStatus = (rawStatus === 'Active' || rawStatus === 'Y')
      ? 'Y'
      : ((rawStatus === 'Resigned' || rawStatus === 'R') ? 'R' : 'N');
    setForm({
      email_sent: emp.emailSent || emp.email_sent || '',
      emp_id: emp.empId || emp.emp_id || '',
      first_name: emp.firstName || emp.first_name || '',
      last_name: emp.lastName || emp.last_name || '',
      email_id: emp.emailId || emp.email_id || '',
      joining_date: formatDateForInput(emp.joiningDate || emp.joining_date),
      experience: emp.experience || '',
      emp_vertical_id: emp.empVerticalId || emp.emp_vertical_id || '',
      joining_type: emp.raw_joining_type || emp.joiningType || emp.joining_type || 'R',
      primary_skills: emp.primarySkills || emp.primary_skills || '',
      secondry_skills: emp.secondrySkills || emp.secondry_skills || '',
      emp_type: emp.empType || emp.emp_type || '',
      shadow_end_date: formatDateForInput(emp.shadowEndDate || emp.shadow_end_date),
      jira_user_id: emp.jiraUserId || emp.jira_user_id || '-',
      overall_experience: emp.overallExperience || emp.overall_experience || '',
      region_id: emp.regionId || emp.region_id || '',
      country_id: emp.countryId || emp.country_id || '',
      department_id: emp.departmentId || emp.department_id || '',
      billability: emp.billability || '',
      emp_designation_id: emp.empDesignationId || emp.emp_designation_id || '',
      effective_from: '',
      experience_category_id: emp.experienceCategoryId || emp.experience_category_id || '',
      location_id: emp.locationId || emp.location_id || '',
      exit_date: formatExitDateForInput(emp.exitDate || emp.exit_date),
      is_active: mappedStatus,
    });
    setFieldErrors({});
    setDialogMode('edit');
  }, []);

  const handleClose = () => {
    setDialogMode(null);
    setSelectedEmployee(null);
    setForm(INITIAL_FORM);
    setFieldErrors({});
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => {
      const updatedForm = { ...prev, [name]: value };
      if (name === 'region_id') {
        updatedForm.country_id = '';
      }
      return updatedForm;
    });
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleDeleteClick = useCallback((emp) => {
    setEmployeeToDelete(emp);
  }, []);

  const confirmDelete = async () => {
    if (!employeeToDelete) return;

    try {
      await UseAxios(deleteEmployee(employeeToDelete.emp_no));
      toast.success('Employee deleted successfully.');
      refreshTable();
    } catch {
      toast.error('Failed to delete employee.');
    } finally {
      setEmployeeToDelete(null);
    }
  };

  const handleCellClick = useCallback(
    (params) => {
      const { action, rowData } = params || {};
      if (action === 'edit') {
        if (!canEdit) {
          toast.error('You do not have permission to edit employees.');
          return;
        }
        handleEditClick(rowData);
      } else if (action === 'delete') {
        if (!canDelete) {
          toast.error('You do not have permission to delete employees.');
          return;
        }
        handleDeleteClick(rowData);
      }
    },
    [handleEditClick, handleDeleteClick, canEdit, canDelete],
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    const errors = validateForm(form);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      toast.error(errors.jira_user_id || 'Please fill in all required fields.');
      return;
    }

    if (dialogMode === 'edit' && form.exit_date && !form.exit_date.startsWith('9999')) {
      const empNo = selectedEmployee?.empNo || selectedEmployee?.emp_no;
      if (empNo) {
        try {
          const checkRes = await UseAxios(getActiveAllocations(empNo, form.exit_date));
          if (checkRes && checkRes.data && checkRes.data.projects && checkRes.data.projects.length > 0) {
            setAllocationWarningProjects(checkRes.data.projects);
            setForm((prev) => ({ ...prev, exit_date: '' }));
            return;
          }
        } catch (error) {
          console.error('Error checking active allocation:', error);
        }
      }
    }

    const updatedByName = userProfile?.user_name || '';

    const selectedVertical = verticals.find(
      (val) => String(val.value) === String(form.emp_vertical_id),
    );
    const selectedDesignation = designations.find(
      (des) => String(des.value) === String(form.emp_designation_id),
    );
    const payload = {
      ...form,
      vertical: selectedVertical ? selectedVertical.label : null,
      designation: selectedDesignation ? selectedDesignation.label : null,
      exit_date: form.exit_date ? form.exit_date : '9999-01-01',
    };

    if (dialogMode === 'edit') {
      payload.updatedBy = updatedByName;
      if (form.effective_from) {
        payload.effectiveFrom = form.effective_from;
      }
    }

    try {
      let res = undefined;
      if (dialogMode === 'add') {
        res = await UseAxios(createEmployee(payload));
      } else {
        res = await UseAxios(updateEmployee(selectedEmployee.emp_no, payload));
      }
      if (
        !res
        || res.isAxiosError
        || res.data?.isAxiosError
        || res instanceof Error
        || res.data instanceof Error
        || res.response?.status >= 400
        || res.data?.response?.status >= 400
        || res.data?.success === false
        || res.success === false
      ) {
        const errorMessage
          = res?.data?.response?.data?.message
          || res?.response?.data?.message
          || res?.data?.message
          || res?.message
          || 'Employee ID already exists or failed to save.';
        toast.error(errorMessage);
        return;
      }
      toast.success(
        dialogMode === 'add'
          ? 'Employee added successfully.'
          : 'Employee updated successfully.',
      );
      handleClose();
      refreshTable();
    } catch (err) {
      const errorMessage
        = err?.response?.data?.message
        || err?.message
        || 'Failed to save employee.';
      toast.error(errorMessage);
    }
  };

  const tableConfig = useMemo(() => {
    const dynamicTableColumns = employeeManagementTableConfig.map((col) => {
      if (col.field === 'action') {
        const allowedActions = [];
        if (canEdit) allowedActions.push('edit');
        if (canDelete) allowedActions.push('delete');
        return {
          ...col,
          actions: allowedActions,
          visible: allowedActions.length > 0,
        };
      }
      return col;
    });

    return {
      generalSettings: {
        enableApiFiltering: true,
        baseURL,
        showPagination: true,
        showToolBar: true,
        userInfo: {
          id: userProfile?.id || '',
          emailId: userProfile?.email || '',
          permissions: userProfile?.permissions || {},
          roleDetails: userProfile?.roleDetails || [],
          employeeId: userProfile?.employeeId || '',
          globalColors: userProfile?.siteConfiguration?.global_colors,
        },
        // Handlers separated!
        handlers: {
          handleAddIconClick: handleAddClick,
          onAddNew: handleAddClick,
        },
        handler: {
          handleFilteredApiResponse,
          onCellClickHandler: handleCellClick,
          setTableStateHandler: (getState) => {
            tableStateRef.current = getState;
          },
        },
        headers: {
          token: userProfile?.access_token || '',
        },
        apiEndpoints: {
          getAllViews: { endpoint: '', enabled: false },
          getFilteredData: { endpoint: '', enabled: true },
          saveOrUpdateView: { endpoint: '', enabled: false },
          deleteView: { endpoint: '', enabled: false },
          download: {
            endpoint: 'export',
            enabled: true,
            filename: () => getExportFileName('Employee_Management'),
            sheetName: () => getExportFileNameWithDate('Employee_Management'),
          },
        },
        toolBarSettings: {
          showAddNew: canAdd, // Use canAdd permission
          showFilterOption: true,
          showDownloadExcel: false,
          showDownloadCsv: true,
          showExpandCollapse: false,
          showColumnCustomize: false,
          showHelp: false,
          showSearch: false,
          showSaveView: false,
          showReset: false,
          showToolBarHeading: true,
          toolBarHeading: 'Employee Management',
          downloadExcelSheetName: 'Employee Management',
        },
      },
      showFilterOption: true,
      tableSettings: {
        tableConfig: dynamicTableColumns,
        size: 'large',
        loaderSkeleton: { rows: 10, bodyCells: 1, columns: 8 },
        enableTreeTable: false,
        pageSize: PAGE_SIZE,
        paginationConfig: {
          ...pagination,
          onPaginationChange: handlePaginationChange,
          rowsPerPageOptions: [10, 15, 25, 50],
        },
        showExpandCollapse: false,
        tableScrollHeight: '800px',
      },
    };
  }, [
    baseURL,
    userProfile,
    handleAddClick,
    handleCellClick,
    handleFilteredApiResponse,
    canAdd,
    canEdit,
    canDelete,
  ]);

  return (
    <div className="emp-mgmt-tu">
      <TableUtility configuration={tableConfig} />
      {dialogMode && (
        <EmployeeFormModal
          mode={dialogMode}
          form={form}
          fieldErrors={fieldErrors}
          verticals={verticals}
          regions={regions}
          countries={countries}
          departments={departments}
          designations={designations}
          experienceCategories={experienceCategories}
          locations={locations}
          onClose={handleClose}
          onSubmit={handleSubmit}
          onChange={handleFormChange}
          onViewHistory={() => {
            if (selectedEmployee) {
              handleClose();
              navigate('/resource/manage-employee-history', {
                state: {
                  empNo: selectedEmployee.emp_no,
                  empName: `${selectedEmployee.first_name || ''} ${selectedEmployee.last_name || ''}`.trim(),
                },
              });
            } else {
              handleClose();
              navigate('/resource/manage-employee-history');
            }
          }}
        />
      )}

      {employeeToDelete && (
        <DeleteConfirmationModal
          empName={`${employeeToDelete.first_name || ''} ${employeeToDelete.last_name || ''}`.trim() || 'this employee'}
          onClose={() => setEmployeeToDelete(null)}
          onConfirm={confirmDelete}
        />
      )}

      {allocationWarningProjects && (
        <AllocationWarningModal
          projects={allocationWarningProjects}
          onClose={() => setAllocationWarningProjects(null)}
        />
      )}
    </div>
  );
};

export default EmployeeManagement;
